import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ChangeDetectorRef
} from '@angular/core';
import { FormControl, ValidatorFn } from '@angular/forms';

@Component({
    selector: 'fcp-table-row',
    templateUrl: './fcp-table-row.component.html',
    styleUrls: ['./fcp-table-row.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FcpTableRowComponent implements OnInit, OnChanges {
    @Input() value = '';
    @Input() state: TableRowState = TableRowState.Default;
    @Input() validators: ValidatorFn[] = [];
    @Input() errorMessages: { [key: string]: string } = {};

    @Output() addValue = new EventEmitter<{ value: string }>();
    @Output() cancel = new EventEmitter<void>();
    @Output() removeValue = new EventEmitter<boolean>();

    public inputControl: FormControl;

    constructor(private cdr: ChangeDetectorRef) {
        this.inputControl = new FormControl('');
    }

    ngOnInit() {
        this.inputControl.setValue(this.value);
        this.inputControl.setValidators([...this.validators]);
        this.cdr.markForCheck();
    }

    ngOnChanges(changes: SimpleChanges) {
        const newValueReceived = changes.value && this.inputControl.value !== this.value;
        if (newValueReceived) {
            this.inputControl.setValue(this.value);
            this.cdr.markForCheck();
        }

        if (changes.value) {
            this.inputControl.updateValueAndValidity();
            this.cdr.markForCheck();
        }
    }

    public onEdit() {
        this.state = TableRowState.Active;
        this.inputControl.updateValueAndValidity();
        this.cdr.markForCheck();
    }

    public onRemove() {
        this.state = TableRowState.Remove;
        this.removeValue.emit(true);
        this.cdr.markForCheck();
    }

    public onAdd() {
        if (this.inputControl.valid && this.hasValueChanged()) {
            const newValue = this.inputControl.value;
            this.addValue.emit(newValue);
            this.value = newValue;
            this.state = TableRowState.Default;
            this.cdr.markForCheck();
        }
    }

    public onCancel() {
        this.inputControl.reset();
        this.inputControl.setValue(this.value);
        this.state = TableRowState.Default;
        this.cancel.emit();
        this.cdr.markForCheck();
    }

    public onUndo() {
        this.state = TableRowState.Default;
        this.removeValue.emit(false);
        this.cdr.markForCheck();
    }

    public maskValue(value: string) {
        this.inputControl.updateValueAndValidity();
        this.inputControl.setValue(value);
        this.cdr.markForCheck();
    }

    public hasValueChanged() {
        return this.inputControl.value !== this.value && this.inputControl.value !== '';
    }

    public getErrorMessage(inputControl: FormControl) {
        const errors = inputControl?.errors;
        if (!errors) {
            return '';
        }

        const errorKey = Object.keys(errors)[0];
        return this.errorMessages[errorKey];
    }
}

export enum TableRowState {
    Default = 'default',
    Active = 'active',
    Remove = 'remove'
}
